/**
 * SquaresGameProgress — iter87.599 (user-requested). Once a Squares
 * game has kicked off, this REPLACES the Schedule tile in the Cheer
 * section: live score + quarter-by-quarter sheet, the locked board
 * with owner initials, the live score's box + checkpoint winners
 * highlighted, hover-for-detail on any box, Total Pot, and a
 * score-likelihood heatmap tint (see squares_heatmap.py backend).
 * Read-only — the Squares modal (Draft section) is unchanged and
 * still owns picking/claiming.
 */
import { Fragment, useCallback, useEffect, useState } from "react";
import { Flame } from "lucide-react";
import { api } from "@/lib/api";

const CHECKPOINT_LABELS = { q1: "Q1", q2: "Halftime", q3: "Q3", final: "Final" };
const PERIODS = ["q1", "q2", "q3", "q4", "ot"];

const TRANCHE_STYLE = {
    high: "bg-rose-500/30",
    moderate_high: "bg-orange-500/20",
    moderate: "bg-amber-400/15",
    moderate_low: "bg-sky-500/20",
    low: "bg-transparent",
};
const TRANCHE_LABEL = { high: "High", moderate_high: "Mod-High", moderate: "Moderate", moderate_low: "Mod-Low", low: "Low" };

export default function SquaresGameProgress({ tournamentId, meId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [hoverIdx, setHoverIdx] = useState(null);

    const load = useCallback(async () => {
        try {
            const d = await api.squaresGetCheckpoints(tournamentId);
            setData(d);
            setLoadError(false);
        } catch (e) {
            setLoadError(true);
        }
        setLoading(false);
    }, [tournamentId]);

    useEffect(() => {
        load();
        const id = setInterval(load, 20000);
        return () => clearInterval(id);
    }, [load]);

    if (loading && !data) {
        return <div className="text-center text-zinc-500 text-sm py-10" data-testid="game-progress-loading">Loading live game…</div>;
    }
    if (loadError && !data) {
        return <div className="text-center text-blaze text-sm py-10" data-testid="game-progress-error">Couldn't load the live game right now — try again shortly.</div>;
    }
    if (!data?.locked) {
        return <div className="text-center text-zinc-500 text-sm py-10" data-testid="game-progress-not-locked">The board hasn't locked yet — check back once it has.</div>;
    }

    const ls = data.live_score;
    const byIdx = {};
    (data.grid || []).forEach((b) => { byIdx[b.box_index] = b; });
    const winnersByIdx = {};
    Object.entries(data.checkpoints || {}).forEach(([cp, r]) => {
        if (r.ready && r.box_index != null) (winnersByIdx[r.box_index] = winnersByIdx[r.box_index] || []).push(cp);
    });
    const hoverBox = hoverIdx != null ? byIdx[hoverIdx] : null;

    return (
        <div className="space-y-4" data-testid="squares-game-progress">
            <div className="rounded-xl border border-white/10 bg-ink p-4" data-testid="game-progress-scoreboard">
                <div className="flex items-center justify-between gap-2">
                    <div className="text-center flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-wide text-cyber mb-1 truncate">{ls?.home_name || "Home"}</div>
                        <div className="text-3xl font-black text-white" data-testid="game-progress-home-score">{ls?.home_score ?? "–"}</div>
                    </div>
                    <div className="text-center shrink-0 px-2">
                        <div className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${ls?.finished ? "bg-amber-400/20 text-amber-300" : "bg-rose-500/20 text-rose-300"}`} data-testid="game-progress-status">
                            {!ls?.finished && <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />}
                            {ls?.finished ? "FINAL" : (ls?.live_detail || ls?.status_short || "LIVE")}
                        </div>
                    </div>
                    <div className="text-center flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-wide text-heat mb-1 truncate">{ls?.away_name || "Away"}</div>
                        <div className="text-3xl font-black text-white" data-testid="game-progress-away-score">{ls?.away_score ?? "–"}</div>
                    </div>
                </div>
                {ls && (
                    <table className="w-full mt-4 text-[11px]" data-testid="game-progress-quarter-sheet">
                        <thead>
                            <tr className="text-zinc-500 uppercase text-[9px]">
                                <th className="text-left font-normal"> </th>
                                {PERIODS.map((p) => (<th key={p} className="font-normal">{p.toUpperCase()}</th>))}
                                <th className="text-volt">T</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="border-t border-white/10">
                                <td className="py-1.5 text-cyber font-bold truncate max-w-[90px]">{ls.home_name}</td>
                                {PERIODS.map((p) => (<td key={p} className="text-center text-zinc-300">{ls.home_period_points?.[p] ?? "–"}</td>))}
                                <td className="text-center font-bold text-volt">{ls.home_score}</td>
                            </tr>
                            <tr className="border-t border-white/5">
                                <td className="py-1.5 text-heat font-bold truncate max-w-[90px]">{ls.away_name}</td>
                                {PERIODS.map((p) => (<td key={p} className="text-center text-zinc-300">{ls.away_period_points?.[p] ?? "–"}</td>))}
                                <td className="text-center font-bold text-volt">{ls.away_score}</td>
                            </tr>
                        </tbody>
                    </table>
                )}
                {!ls && (
                    <p className="text-center text-xs text-zinc-500 mt-3" data-testid="game-progress-no-live">
                        Live score unavailable right now — checking back shortly.
                    </p>
                )}
            </div>

            {data.live_pot && (
                <div className="rounded-lg border border-volt/30 bg-volt/[0.05] p-3 flex items-center justify-between gap-3" data-testid="game-progress-pot">
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500">Total Pot {data.full_locked ? "(final)" : "(live)"}</div>
                    <div className="text-right">
                        <div className="font-bold text-volt" data-testid="game-progress-pot-amount">{data.live_pot.net_pot} tokens</div>
                        <div className="text-[10px] text-zinc-500">{data.live_pot.n_confirmed} confirmed</div>
                    </div>
                </div>
            )}

            <div className="rounded-lg border border-white/10 bg-ink p-3" data-testid="game-progress-grid-card">
                <div className="flex items-center justify-between gap-2 mb-2 min-h-[28px]">
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500 shrink-0">Locked Board</div>
                    {hoverBox ? (
                        <div className="text-[11px] font-bold text-white text-right" data-testid="game-progress-hover-detail">
                            {ls?.home_name || "Home"} {hoverBox.home_digit} · {ls?.away_name || "Away"} {hoverBox.away_digit}
                            {hoverBox.username ? ` — ${hoverBox.username}${hoverBox.participant_id === meId ? " (You!)" : ""}` : " — unclaimed"}
                        </div>
                    ) : (
                        <div className="text-[10px] text-zinc-600">Hover or tap a box for detail</div>
                    )}
                </div>
                <div className="overflow-x-auto flex justify-center">
                    <div className="inline-grid gap-[2px]" style={{ gridTemplateColumns: "18px repeat(10, 25px)" }}>
                        <div />
                        {Array.from({ length: 10 }).map((_, c) => (
                            <div key={`h-${c}`} className="text-center text-[10px] font-bold text-cyber">{byIdx[c]?.home_digit ?? "?"}</div>
                        ))}
                        {Array.from({ length: 10 }).map((_, r) => (
                            <Fragment key={`row-${r}`}>
                                <div className="text-center text-[10px] font-bold text-heat flex items-center justify-center">{byIdx[r * 10]?.away_digit ?? "?"}</div>
                                {Array.from({ length: 10 }).map((_, c) => {
                                    const idx = r * 10 + c;
                                    const box = byIdx[idx];
                                    const wins = winnersByIdx[idx];
                                    const isLive = data.live_box_index === idx;
                                    const tint = TRANCHE_STYLE[box?.tranche] || "";
                                    const winRing = wins?.includes("final")
                                        ? "border-amber-300 shadow-[0_0_8px_-1px_rgba(252,211,77,0.85)]"
                                        : wins?.length ? "border-cyan-400" : "border-zinc-700";
                                    const mine = box?.participant_id === meId;
                                    return (
                                        <button
                                            key={idx}
                                            type="button"
                                            data-testid={`game-progress-box-${idx}`}
                                            data-box-tranche={box?.tranche || ""}
                                            // iter87.599 post-test fix (testing_agent HIGH finding)
                                            // — on touch devices the browser fires a compatibility
                                            // mouseenter right before click, so a toggle-on-click
                                            // immediately undid the hover-set. onPointerEnter/Leave
                                            // now only drive hover for an actual mouse; click always
                                            // just SETS the index (no toggle), which works for both
                                            // a mouse click and a touch tap.
                                            onPointerEnter={(e) => { if (e.pointerType === "mouse") setHoverIdx(idx); }}
                                            onPointerLeave={(e) => { if (e.pointerType === "mouse") setHoverIdx((v) => (v === idx ? null : v)); }}
                                            onClick={() => setHoverIdx(idx)}
                                            className={`relative w-[25px] h-[25px] rounded-sm border-2 text-[8px] font-bold flex items-center justify-center transition ${tint} ${winRing} ${mine ? "text-volt" : "text-zinc-300"}`}
                                        >
                                            {box?.initials || ""}
                                            {isLive && <span className="absolute inset-0 rounded-sm ring-2 ring-white animate-pulse" />}
                                        </button>
                                    );
                                })}
                            </Fragment>
                        ))}
                    </div>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[9px] uppercase tracking-wide text-zinc-500 mt-3">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm ring-2 ring-white" /> Live score</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm border-2 border-cyan-400" /> Checkpoint win</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm border-2 border-amber-300" /> Final win</span>
                </div>
                <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[9px] uppercase tracking-wide text-zinc-500 mt-1.5">
                    {Object.entries(TRANCHE_LABEL).map(([k, label]) => (
                        <span key={k} className="flex items-center gap-1">
                            <span className={`w-2 h-2 rounded-sm ${TRANCHE_STYLE[k]} border border-white/20`} /> {label}
                        </span>
                    ))}
                </div>
                <p className="text-center text-[9px] text-zinc-500 mt-1.5" data-testid="game-progress-heatmap-disclaimer">
                    Heatmap is a rough guide based on the current score — not a guarantee.
                </p>
            </div>

            {data.checkpoints && (
                <div className="rounded-lg border border-white/10 bg-ink p-3" data-testid="game-progress-checkpoints-card">
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-2 flex items-center gap-1.5">
                        <Flame className="w-3 h-3" /> Checkpoint Winners
                    </div>
                    <div className="space-y-1.5">
                        {Object.entries(data.checkpoints).map(([cp, r]) => (
                            <div key={cp} className="flex items-start justify-between gap-2 text-sm" data-testid={`game-progress-checkpoint-${cp}`}>
                                <span className="uppercase text-[11px] font-bold text-zinc-400 shrink-0 pt-0.5">{CHECKPOINT_LABELS[cp] || cp.toUpperCase()}</span>
                                {!r.ready ? (
                                    <span className="text-zinc-400 text-xs">Pending</span>
                                ) : (
                                    <span className={`text-xs font-bold text-right ${r.participant_id === meId ? "text-volt" : "text-zinc-300"}`}>
                                        {ls?.away_name || "Away"} {r.away_score} – {ls?.home_name || "Home"} {r.home_score} → {r.username ? `${r.username}${r.participant_id === meId ? " (You!)" : ""}` : "unclaimed box"}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
